import { useState, useRef } from 'react';
import { Heart } from 'lucide-react';
import { SearchResult } from '#server/models';
import {
  ResultCard,
  ResultCardDescription,
  ResultCardHeader,
  ResultCardTitle,
  ResultCardLink,
  ResultCardTag,
  ResultCardContent,
  Badge,
  Divider,
  HighlightedText,
} from '@/components';
import { cn } from '@/utils';
import { useDeviceType, useFavourites } from '@/hooks';
import { formatDeliveryModes, formatLocations } from './helpers.ts';
import { BookIcon, BridgingIcon, GraduationCapIcon, MicroscopeIcon } from './CourseIcons';

export interface CourseResultCardProps {
  result: SearchResult;
  showCourseBadge: boolean;
}

const courseTypeConfig: Record<string, { color: string; icon: React.ReactNode }> = {
  Undergraduate: {
    color: 'bg-badge-yellow leading-3 hover:bg-badge-yellow text-black',
    icon: <BookIcon className="h-3 w-3" />,
  },
  Postgraduate: {
    color: 'bg-badge-purple leading-3 hover:bg-badge-purple text-black',
    icon: <GraduationCapIcon className="h-3 w-3" />,
  },
  'Bridging and Enabling': {
    color: 'bg-badge-pink leading-3 hover:bg-badge-pink text-black',
    icon: <BridgingIcon className="h-3 w-3" />,
  },
  Research: {
    color: 'bg-badge-green leading-3 hover:bg-badge-green text-black',
    icon: <MicroscopeIcon className="h-3 w-3" />,
  },
};

const CourseComponentPanel = ({
  title,
  courseComponents,
  forYou,
  isDesktop,
}: {
  title: string;
  courseComponents: NonNullable<SearchResult['courseDetails']>['majors'];
  forYou?: boolean | undefined;
  isDesktop?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasMoreThanFour = courseComponents.length > 4;
  const shouldShowTruncated = !isExpanded && hasMoreThanFour && isDesktop;
  const shouldShowExpandButton = !isExpanded && (hasMoreThanFour || !isDesktop);
  const shouldShowAllItems = isExpanded || (!hasMoreThanFour && isDesktop);

  const itemsToShow = shouldShowTruncated ? courseComponents.slice(0, 4) : courseComponents;
  const moreCount = isDesktop ? courseComponents.length - 4 : courseComponents.length;

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);
    if (!isDesktop && containerRef.current) {
      setTimeout(() => {
        // Find the parent card element
        const cardElement = containerRef.current!.closest('[data-slot="card"]');
        if (cardElement) {
          const rect = cardElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + scrollTop;

          // Calculate offset: check for sticky headers
          const stickyHeader = document.querySelector('header');
          const headerHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
          const offset = headerHeight;

          window.scrollTo({
            top: elementTop - offset,
            behavior: 'smooth',
          });
        }
      }, 0);
    }
  };

  return (
    <>
      <div ref={containerRef} className="my-4 flex gap-6 text-sm">
        <div className="w-[6rem] shrink-0 font-[600]">{title}</div>
        <ul className="flex list-disc flex-wrap gap-2 marker:text-marker-grey">
          {shouldShowTruncated &&
            itemsToShow.map((major, i) => (
              <li key={i} className="ml-4">
                <a
                  href={major.url}
                  target="_blank"
                  className="text-xs font-semibold underline hover:text-deep-blue focus:text-deep-blue"
                >
                  {major.title}
                </a>
              </li>
            ))}

          {shouldShowExpandButton && (
            <li className={isDesktop ? 'ml-4' : 'ml-0 list-none'}>
              <span
                className="cursor-pointer text-xs font-semibold underline hover:text-deep-blue focus:text-deep-blue"
                tabIndex={0}
                onClick={handleExpand}
              >
                +{moreCount} more
              </span>
            </li>
          )}

          {shouldShowAllItems &&
            courseComponents.map((major, i) => (
              <li key={i} className="ml-4">
                <a
                  href={major.url}
                  target="_blank"
                  className="text-xs font-semibold underline hover:text-deep-blue focus:text-deep-blue"
                >
                  {major.title}
                </a>
              </li>
            ))}

          {isExpanded && (
            <li className="ml-4">
              <span
                className="cursor-pointer text-xs font-semibold underline hover:text-deep-blue focus:text-deep-blue"
                tabIndex={0}
                onClick={handleCollapse}
              >
                View less
              </span>
            </li>
          )}
        </ul>
      </div>
      <Divider className={cn(forYou && 'bg-highlight-divider')} />
    </>
  );
};

export const CourseResultCard = ({ result, showCourseBadge }: CourseResultCardProps) => {
  const typeConfig = courseTypeConfig[result.courseDetails!.offeringLevel] || {
    color: 'bg-gray-200 text-black',
    icon: <BookIcon className="h-3 w-3" />,
  };

  const courseDetails = result.courseDetails!;

  const { isFavourite, toggleFavourite } = useFavourites();
  const { isDesktop } = useDeviceType();

  return (
    <ResultCard
      className={cn(
        'focus-within:border-focus-ring hover:border-ring md:pt-0',
        result.forYou && 'border-highlight-border bg-highlight-bg',
      )}
    >
      <ResultCardLink to={courseDetails.url} title={courseDetails.title} result={result}>
        <ResultCardHeader className="pt-6">
          <ResultCardTag className="flex justify-between">
            <div className="flex gap-2">
              {(result.forYou ?? false) && <Badge className="mr-2 bg-ring text-white">For You</Badge>}
              {showCourseBadge && <Badge variant={(result.forYou ?? false) ? 'forYou' : 'secondary'}>Course</Badge>}
              {courseDetails.offeringLevel && (
                <Badge variant="secondary" className={cn(typeConfig.color, 'flex items-center gap-1')}>
                  <span className="pb-[1px]">{typeConfig.icon}</span>
                  <span>{courseDetails.offeringLevel}</span>
                </Badge>
              )}
            </div>
            {courseDetails.courseType && (
              <Badge variant="outline" className="leading-3">
                {courseDetails.courseType}
              </Badge>
            )}
          </ResultCardTag>
          <ResultCardTitle>{courseDetails.title}</ResultCardTitle>
          <ResultCardDescription className="mb-3">
            <HighlightedText text={courseDetails.description} />
          </ResultCardDescription>
          <Divider className={cn(result.forYou && 'bg-highlight-divider')} />
          {(courseDetails.atar ||
            courseDetails.duration ||
            courseDetails.deliveryModes.length > 0 ||
            courseDetails.locations.length > 0) && (
              <>
                <div className="my-4 flex flex-col gap-4 text-sm md:grid md:grid-cols-2">
                  {courseDetails.atar && (
                    <div className="flex gap-6">
                      <div className="w-[6rem] shrink-0 font-[600]">
                        {courseDetails.atar.type === 'MINIMUM' ? 'Minimum ATAR' : 'ATAR'}
                      </div>
                      <div className="text-gray-500">{courseDetails.atar.value}</div>
                    </div>
                  )}
                  {courseDetails.duration && (
                    <div className="flex gap-6">
                      <div className="w-[6rem] shrink-0 font-[600]">Duration</div>
                      <div className="text-gray-500">{courseDetails.duration}</div>
                    </div>
                  )}
                  {courseDetails.deliveryModes.length > 0 && (
                    <div className="flex gap-6">
                      <div className="w-[6rem] shrink-0 font-[600]">Attendance mode</div>
                      <div className="text-gray-500">{formatDeliveryModes(courseDetails.deliveryModes)}</div>
                    </div>
                  )}
                  {courseDetails.locations.length > 0 && (
                    <div className="flex gap-6">
                      <div className="w-[6rem] shrink-0 font-[600]">Location</div>
                      <div className="text-gray-500">{formatLocations(courseDetails.locations)}</div>
                    </div>
                  )}
                </div>
                <Divider className={cn(result.forYou && 'bg-highlight-divider')} />
              </>
            )}
        </ResultCardHeader>
      </ResultCardLink>
      <ResultCardContent className="mt-2">
        {courseDetails.majors && courseDetails.majors.length > 0 && (
          <CourseComponentPanel
            title={'Majors'}
            courseComponents={courseDetails.majors}
            forYou={result.forYou}
            isDesktop={isDesktop}
          />
        )}
        {courseDetails.availableIn && courseDetails.availableIn.length > 0 && (
          <CourseComponentPanel
            title={'Available in'}
            courseComponents={courseDetails.availableIn}
            forYou={result.forYou}
            isDesktop={isDesktop}
          />
        )}

        <div className="flex items-center gap-2 pt-4">
          <button
            className="group cursor-pointer"
            aria-label="Toggle Favourite"
            onClick={() => toggleFavourite(result.id)}
          >
            <Heart
              className={cn(
                'w-[19px] transition-colors',
                cn(
                  isFavourite(result.id)
                    ? 'fill-strawberry text-strawberry'
                    : 'text-muted-foreground group-hover:text-strawberry',
                ),
              )}
            />
          </button>
          <span className="text-sm font-medium">Favourite</span>
        </div>
      </ResultCardContent>
    </ResultCard>
  );
};

export const FavouriteCourseResultCard = ({ result }: { result: SearchResult }) => {
  const typeConfig = courseTypeConfig[result.courseDetails!.offeringLevel] || {
    color: 'bg-gray-200 text-black',
    icon: <BookIcon className="h-3 w-3" />,
  };

  const courseDetails = result.courseDetails!;

  const { isFavourite, toggleFavourite } = useFavourites();

  return (
    <ResultCard
      className={cn(
        'bg-white focus-within:border-focus-ring hover:border-ring md:pt-0',
        result.forYou && 'border-highlight-border bg-highlight-bg',
      )}
    >
      <ResultCardLink to={courseDetails.url} title={courseDetails.title} result={result}>
        <ResultCardHeader className="pt-4">
          <ResultCardTag className="flex justify-between">
            <div className="flex gap-2">
              {courseDetails.offeringLevel && (
                <Badge variant="secondary" className={cn(typeConfig.color, 'flex items-center gap-1')}>
                  <span className="pb-[1px]">{typeConfig.icon}</span>
                  <span>{courseDetails.offeringLevel}</span>
                </Badge>
              )}
            </div>
            {courseDetails.courseType && (
              <Badge variant="outline" className="leading-3">
                {courseDetails.courseType}
              </Badge>
            )}
          </ResultCardTag>

          <ResultCardTitle>{courseDetails.title}</ResultCardTitle>
        </ResultCardHeader>
      </ResultCardLink>
      <ResultCardContent className="mt-auto">
        <div className="flex items-center gap-2 pt-4">
          <button
            className="group cursor-pointer"
            aria-label="Toggle Favourite"
            onClick={() => toggleFavourite(result.id)}
          >
            <Heart
              className={cn(
                'w-[19px] transition-colors',
                cn(
                  isFavourite(result.id)
                    ? 'fill-strawberry text-strawberry'
                    : 'text-muted-foreground group-hover:text-strawberry',
                ),
              )}
            />
          </button>
          {/* <span className="text-sm font-medium">Favourite</span> */}
        </div>
      </ResultCardContent>
    </ResultCard>
  );
};
